import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StudentTest, StudentTestEditResource, StudentTestViewResource } from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentTestPageBody } from "./StudentTestPageBody";
import { StudentTestPageHeader } from "./StudentTestPageHeader";

const routeName = "studentTest";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
  schoolYear?: number = 0;
}

export const studentTestPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Test",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentTestPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentTest;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [test, setTest] = useState<StudentTest>({} as StudentTest);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [test] });
  const [viewResource, setViewResource] = useState<StudentTestViewResource>({});
  const [editResource, setEditResource] = useState<StudentTestEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  const [studentId] = useState<number | undefined>(optionalParams.studentId);
  const [schoolYear] = useState<number | undefined>(optionalParams.schoolYear);

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${studentTestPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.studentTests.get(id).then((response) => {
      if (id === 0) {
        if (studentId) response.studentId = studentId;
        if (schoolYear) response.year = schoolYear;
      }

      setTest(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentTests.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentTests.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentTestPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentTestPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      test={test}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentTestPageBody
      crudMode={params.crudMode}
      test={test}
      setTest={setTest}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
